import type { FC } from 'react';
import { Suspense } from 'react';
import { Navigate, Route, useLocation } from 'react-router-dom';
import { useAssignmentServicePublic } from 'src/apis/assignment-service/use-assignment-service-root';
import { useAuthorizationService } from 'src/apis/authorization-service/use-authorization-service';
import { useCapacityManagement } from 'src/apis/capacity-management/use-capacity-management';
import { useOrganisationService } from 'src/apis/organisation-service/use-organisation-service';
import { useRegularFcfsRoot } from 'src/apis/regular-fcfs/use-regular-fcfs-root';
import { useRemitReportingEntrypoint } from 'src/apis/remit-reporting/use-remit-reporting-entrypoint';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { UserMenu } from 'src/components/layout/header/user-menu';
import {
  LayoutContainer,
  LayoutContent,
  LayoutSidebar,
  Sidebar,
} from 'src/components/layout/sidebar';
import { RootRoutes } from 'src/components/root-routes';
import { PageSpinner } from 'src/components/spinner-container';
import { Sticky } from 'src/components/sticky';
import { useDirectFcfsFlags } from 'src/hooks/use-direct-fcfs-flags';
import { Platform } from 'src/pages/platform';
import { AssignmentSettings } from 'src/pages/settings/assignment-settings';
import { AuctionsSettings } from 'src/pages/settings/auctions';
import { DirectFcfsSettings } from 'src/pages/settings/direct-fcfs';
import { GeneralTransportSettings } from 'src/pages/settings/general-transport-settings';
import { NotificationSettings } from 'src/pages/settings/notifications';
import { OrganisationManagement } from 'src/pages/settings/organisation';
import { PermissionGroups } from 'src/pages/settings/permission-groups';
import { PremiumServices } from 'src/pages/settings/premium-services';
import { Profile } from 'src/pages/settings/profile';
import { RegularFcfsSettings } from 'src/pages/settings/regular-fcfs';
import { RemitReportingSettings } from 'src/pages/settings/remit-reporting';
import { StandardReportSettings } from 'src/pages/settings/reporting/standard-reports';

export const Settings: FC = () => {
  const authorizationService = useAuthorizationService();
  const organisationService = useOrganisationService();
  const capacityManagementService = useCapacityManagement();
  const regularFcfsRoot = useRegularFcfsRoot();
  const directFcfsFlags = useDirectFcfsFlags();
  const assignmentService = useAssignmentServicePublic();
  const remitReportingEntrypoint = useRemitReportingEntrypoint();
  if (
    authorizationService.response === null ||
    !organisationService.response ||
    !regularFcfsRoot.loaded ||
    !remitReportingEntrypoint.loaded
  )
    return <PageSpinner />;

  return (
    <LayoutContainer>
      <LayoutSidebar>
        <Sticky>
          <Sidebar>
            <UserMenu
              authorizationService={authorizationService.response.data}
              organisationService={organisationService.response.data}
              capacityManagementService={capacityManagementService ?? null}
              regularFcfsService={regularFcfsRoot.response?.data}
              directFcfsService={directFcfsFlags.data}
              assignmentService={assignmentService.response?.data ?? null}
              remitReportingService={remitReportingEntrypoint.response?.data}
              show={{
                personalSettings: true,
                companySettings: true,
                transportSettings: true,
                storageSettings: true,
                shipperSettings: true,
                otherSettings: false,
                reportingSettings: true,
              }}
            />
          </Sidebar>
        </Sticky>
      </LayoutSidebar>

      <LayoutContent>
        <Suspense fallback={<PageSpinner />}>
          <RootRoutes>
            <Route path="/settings/profile" element={<Profile />} />

            <Route
              path="/settings/notifications/*"
              element={<NotificationSettings />}
            />

            <Route
              path="/settings/organisation/*"
              element={<OrganisationManagement />}
            />

            <Route
              path="/settings/permission-groups/*"
              element={<PermissionGroups />}
            />

            <Route
              path="/settings/assignment-settings/*"
              element={<AssignmentSettings />}
            />

            <Route path="/settings/platform" element={<Platform />} />

            <Route
              path="/settings/regular-fcfs/*"
              element={<RegularFcfsSettings />}
            />

            <Route
              path="/settings/direct-fcfs"
              element={<DirectFcfsSettings />}
            />

            <Route
              path="/settings/premium-services"
              element={<PremiumServices />}
            />

            <Route
              path="/settings/general-transport-settings"
              element={<GeneralTransportSettings />}
            />

            <Route
              path="/settings/reporting/standard-reports"
              element={<StandardReportSettings />}
            />

            <Route path="/settings/auctions" element={<AuctionsSettings />} />

            <Route
              path="/settings/remit-reporting/*"
              element={<RemitReportingSettings />}
            />

            <Route path="*" element={<ThrowNotFound />} />
          </RootRoutes>
        </Suspense>
      </LayoutContent>
    </LayoutContainer>
  );
};

export const ProfileSettingsRedirect: FC = () => {
  const { pathname, search, state, hash } = useLocation();
  return (
    <Navigate
      replace
      to={{
        pathname: pathname.replace('/profile-settings', '/settings'),
        search,
        hash,
      }}
      state={state}
    />
  );
};
